import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { userToken, logout, isLoggedIn } from "../../features/user";
import axios from "axios";
import { API_OK, API_URL, DEFAULT_ERROR_TEXT, API_EXCEPTION } from '../../Constants';
import { Trans, useTranslation } from 'react-i18next';
import { checkApiSessionErrorMessage, checkValidToken, getLang, landOrResolve } from "../../Helper";
import Frame from '../widgets/Frame';
import VoiceCallNotice from '../widgets/VoiceCallNotice';
import { useAutoFocus } from "../../hooks/useAutoFocus";
import { useWebOtp } from "../../hooks/useWebOtp";
import { useRetryCountdown } from "../../hooks/useRetryCountdown";
import RetryNotice from "../widgets/RetryNotice";
import { useEnterSubmit } from '../../hooks/useEnterSubmit';

/**
 * אימות אמצעי תקשורת - המסך שמוציא מהחסימה.
 *
 * **למה הוא כאן ולא באזור האישי.** הסשן שנוצר בהתחברות תקף לחלוטין, ואינו פותח דבר עד
 * שאומת אמצעי תקשורת: `SsoAuthMiddleware` דוחה כל בקשה לכל שירות. כלומר האזור האישי
 * אינו יכול לארח את המסך הזה - הוא עצמו חסום, וכל טעינה שלו הייתה מוחזרת לכאן. זו
 * בדיוק אותה סיבה שרישום שיטת אימות דו-שלבי חובה יושב ב-`/two-step-verification`
 * ולא ב-myyemot.
 *
 * (המסך המקביל ב-myyemot, ב-`/profile-settings`, הוא למי שכבר עומד בדרישה ורוצה
 * לשנות פרט מרצונו. שני מסכים לשתי שאלות שונות.)
 *
 * **שתי דרכים החוצה, ושתיהן מוצגות תמיד**: לאמת את הפרט שכבר בחשבון, או להחליף אותו
 * ולאמת את החדש. לקוח שהנציג הקליד לו מייל שגוי אינו יכול לאמת אותו לעולם, ומסך
 * שמציע לו רק "שלח קוד" נועל אותו בחוץ - וזה ההבדל המהותי מרישום 2FA, שם הדרך
 * היחידה החוצה היא לרשום שיטה והיא תמיד זמינה.
 */

/** קישור שאין בו טעם ללחוץ עכשיו - מעומעם, ולא נעלם: היעלמות נקראת כתקלה. */
const DISABLED_LINK = { opacity: 0.5, pointerEvents: "none" };

/** שלב במסך. `pick` = בוחרים ערוץ ויעד, `code` = מקישים את הקוד. */
const PHASE_PICK = "pick";
const PHASE_CODE = "code";

const EMAIL = "EMAIL";
const PHONE = "PHONE";

export function VerifyContact() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isUserLoggedIn = useSelector(isLoggedIn);

    const [state, setState] = useState({
        loading: true,
        channels: [],
        /** הערוצים שנותרו לאמת, בסדר שבו עוברים עליהם. */
        outstanding: [],
        phase: PHASE_PICK,
        channel: null,
        /** ריק = אימות הפרט הקיים. ערך = החלפה. */
        changing: false,
        destination: "",
        phoneMethod: "SMS",
        code: "",
        /** היעד שאליו הקוד יצא, כדי שמסך הקוד יאמר לאן. */
        sentTo: "",
        /**
         * השיטה שבה הקוד יצא **בפועל** - ולא `phoneMethod`, שהוא הבורר שבשלב הקודם.
         *
         * שלב ההקלדה נראה אחרת בשיחה, ולכן הוא חייב לדעת מה נשלח. גזירה מ-`phoneMethod`
         * הייתה נכונה רק כל עוד איש לא נגע בבורר אחרי השליחה - וכשמישהו נגע, המסך היה
         * מסביר את הקוד שביד לפי ערוץ שלא נשלח בו דבר. ריק = עוד לא יצאה שליחה.
         */
        sentMethod: null,
        busy: false,
        error: null,
        /**
         * אישור על פעולה שהצליחה, ולא שגיאה - ולכן שדה נפרד.
         *
         * הוא קיים בשביל "שלחו לי שוב": המסך אחרי שליחה חוזרת נראה זהה למסך שלפניה,
         * ולקוח שאינו רואה שקרה משהו לוחץ שוב. כל לחיצה כזו שורפת אחת מחמש השליחות
         * שבחלון, ובסוף תולה על הלקוח חסימה של רבע שעה - במסך שהוא ממילא חסום בו.
         */
        notice: null,
    });

    /**
     * הספירה לאחור עד שאפשר לבקש קוד נוסף.
     *
     * השרת מוסר `tryAgainInMinutes` יחד עם הסירוב, והמסך מציג ממנו זמן שרץ. בלי זה
     * ההודעה יכולה לומר רק "נסה מאוחר יותר", והלקוח מנחש - בדרך כלל בלחיצות נוספות,
     * שכל אחת מהן מתקרבת שוב לתקרה.
     */
    const sendRetry = useRetryCountdown();
    /**
     * ספירה שנייה, לניחוש הקוד.
     *
     * נפרדת מזו של השליחה, כי הן שתי תקרות שונות עם שתי משמעויות שונות: האחת מגבילה
     * כמה הודעות יוצאות, והשנייה כמה פעמים מותר לטעות בקוד. ספירה אחת הייתה חוסמת את
     * הכפתור הלא נכון - טעות בקוד הייתה נועלת את "שליחת קוד", שהוא בדיוק המוצא.
     */
    const submitRetry = useRetryCountdown();

    /*
     * הסמן בשדה הקוד ברגע שהוא נפתח - והמסך הזה הוא הטעם העיקרי לכך.
     *
     * שלב הקוד כאן אינו מסך חדש אלא החלפה בתוך אותו מסך: הלקוח לוחץ "שליחת קוד",
     * והשדה נפתח בדיוק במקום שהאצבע לחצה בו - בלי סמן, ובמובייל בלי מקלדת.
     *
     * `focusCode` נקרא בכל שליחה מוצלחת, בשביל "שלחו לי שוב": שם השלב אינו משתנה,
     * הקוד שבשדה נמחק, והסמן נשאר על הקישור שבתחתית המסך.
     */
    const [codeRef, focusCode] = useAutoFocus(state.phase === PHASE_CODE ? PHASE_CODE : null);

    /**
     * מילוי אוטומטי של הקוד שהגיע במסרון.
     *
     * `sentMethod` הוא מה שיצא בפועל, ולא מה שנבחר בבורר - אותה הבחנה שבגללה הודעת
     * השיחה מוצגת לפיו. במייל ובשיחה אין מסרון, וההאזנה אינה נפתחת.
     */
    useWebOtp(
        state.phase === PHASE_CODE && state.sentMethod === 'SMS',
        (code) => setState((prev) => ({ ...prev, code })),
        () => confirmCode()
    );

    const requestConfig = () => ({ 'headers': { "token": userToken(), "lang": getLang() } });

    useEffect(() => {
        if (isUserLoggedIn) {
            /**
             * `forward: false` - אנחנו כבר על המסך שצריך להיות מוצג, ואסור שהבדיקה
             * תעביר את הלקוח לממשק שממילא יחזיר אותו לכאן. אותה קריאה בדיוק כמו
             * ב-`/two-step-verification`, ומאותו טעם.
             */
            checkValidToken(navigate, dispatch, logout, false, true, "/login");
            loadState();
        } else {
            navigate("/login");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showReplyError = (reply) => {
        let errorMessage = DEFAULT_ERROR_TEXT;
        if ((reply?.data?.responseStatus === API_EXCEPTION) && reply.data.hasOwnProperty("message")) {
            errorMessage = reply.data["message"];
        }
        if (checkApiSessionErrorMessage(errorMessage, dispatch, logout, navigate)) {
            return;
        }
        /**
         * הסיבה קודמת להודעה.
         *
         * השרת מוסר `reason` מובנה לצד טקסט לקורא אנושי, והמסך בוחר את הנוסח שלו -
         * בעיקר עבור "כבר תפוס", שאינו תקלה אלא מצב שיש ממנו המשך.
         */
        // התקרה מדווחת בדקות שבריריות, והווידג'ט מציג מהן זמן שרץ.
        if (reply?.data?.hasOwnProperty("tryAgainInMinutes")) {
            const target = reply?.data?.message === "max_trials_in_period" ? submitRetry : sendRetry;
            target.startFromMinutes(reply.data["tryAgainInMinutes"]);
        }

        /**
         * ברמה העליונה של הגוף, ולא תחת `extraData`.
         *
         * `Routes.ts` פורש את ה-extraData לתוך התשובה (`{...json, ...extraData}`), ולכן
         * קריאה מקוננת החזירה `undefined` תמיד - והמסך נפל למחרוזת הגולמית של השרת,
         * שאין לה תרגום ולכן הוצגה כמות שהיא.
         */
        const reason = reply?.data?.reason;

        /**
         * "כבר בשימוש" מחזיר את המסך לבחירת ערך - כי זו הפעולה היחידה שיש ממנה המשך.
         *
         * הסירוב הזה מגיע **אחרי** שהקוד אושר (הבדיקה נעשית רק שם, כדי שהמסלול לא
         * יהפוך למונה כתובות), כלומר הלקוח עומד במסך הקלדת הקוד עם שגיאה - ומסך שנשאר
         * שם מציג לו הודעה שאין עליה מה לעשות, בזמן שהמוצא היחיד הוא לחיצה על "חזרה"
         * שאינה נראית קשורה. הוא מוחזר לשלב הבחירה, ישר על הזנת ערך אחר, וההודעה
         * נשארת מוצגת שם.
         */
        if (reason === "CONTACT_ALREADY_IN_USE") {
            setState((prev) => ({
                ...prev,
                busy: false,
                phase: PHASE_PICK,
                changing: true,
                destination: "",
                code: "",
                error: reason,
                notice: null,
            }));
            return;
        }

        setState((prev) => ({ ...prev, busy: false, error: reason || errorMessage, notice: null }));
    };

    const showRequestError = () => {
        setState((prev) => ({ ...prev, busy: false, error: DEFAULT_ERROR_TEXT, notice: null }));
    };

    const loadState = async () => {
        try {
            /**
             * `busy` מתאפס כאן, ולא רק בענפי השגיאה.
             *
             * אחרי אישור מוצלח המסך נטען מחדש כדי לעבור לערוץ הבא, ו-`confirmCode`
             * מחזיר בלי לאפס - כלומר הכפתור נשאר "שולחים..." על המסך החדש, ותקוע.
             */
            setState((prev) => ({ ...prev, busy: false }));
            const reply = await axios.post(API_URL + 'getContactVerificationState', {}, requestConfig());
            if (reply?.data?.responseStatus === API_OK) {
                const channels = reply.data.channels ?? [];
                const outstanding = reply.data.outstanding ?? [];
                /**
                 * אם כבר יש ערוץ מאומת - אין מה לעשות כאן, וממשיכים.
                 *
                 * קורה כשהלקוח אימת בלשונית אחרת, או כשהריסיילר כיבה את הדרישה בזמן
                 * שהמסך היה פתוח. מסך שהיה נשאר היה מבקש לאמת משהו שכבר אומת.
                 */
                if (reply.data.owesVerification === false) {
                    leaveVerification(reply.data);
                    return;
                }
                /**
                 * הערוץ שמוצג הוא הראשון **שנותר לאמת**, ולא סתם הראשון ברשימה.
                 *
                 * הריסיילר יכול לחייב את שניהם - זו ברירת המחדל, כמו בהרשמה - ואז
                 * המסך עובר עליהם בזה אחר זה. לקוח שאימת מייל וחייב גם טלפון ממשיך
                 * לטלפון, ואינו נוחת בממשק שיחזיר אותו לכאן.
                 */
                const nextChannel = outstanding[0] ?? null;
                const next = channels.find((c) => c.channel === nextChannel) ?? null;
                setState((prev) => ({
                    ...prev,
                    loading: false,
                    channels: channels,
                    outstanding: outstanding,
                    phase: PHASE_PICK,
                    channel: next ? next.channel : EMAIL,
                    changing: next ? !next.current : true,
                    destination: "",
                    code: "",
                    error: null,
                    notice: null,
                    sentMethod: null,
                }));
                return;
            }
            showReplyError(reply);
            setState((prev) => ({ ...prev, loading: false }));
        } catch (e) {
            showRequestError();
            setState((prev) => ({ ...prev, loading: false }));
        }
    };

    const channelState = (channel) => state.channels.find((c) => c.channel === channel) ?? null;

    const selectChannel = (channel) => {
        sendRetry.reset();
        const current = channelState(channel)?.current;
        setState((prev) => ({
            ...prev,
            channel: channel,
            // ערוץ שאין בו פרט נפתח תמיד על החלפה: אין מה לאמת.
            changing: !current,
            destination: "",
            phoneMethod: "SMS",
            error: null,
            notice: null,
            sentMethod: null,
        }));
    };

    const setChanging = (changing) => {
        setState((prev) => ({ ...prev, changing: changing, destination: "", error: null, notice: null, sentMethod: null }));
    };

    /**
     * שולח את הקוד - בפעם הראשונה, ובכל פעם נוספת.
     *
     * `isResend` משנה דבר אחד בלבד: את ההודעה שנשארת על המסך אחרי ההצלחה. הקריאה
     * עצמה זהה, וזה מה שנכון - "שלחו לי שוב" הוא בדיוק אותה שליחה לאותו יעד, ולא
     * מסלול שני שצריך להסכים עם הראשון.
     *
     * הערוץ, השיטה והיעד נשארים ב-`state` גם אחרי המעבר לשלב הקוד, ולכן אין צורך
     * למסור אותם שוב - וגם אין דרך שהם יסטו ממה שנשלח בפעם הקודמת.
     */
    const sendCode = async (isResend = false) => {
        if (state.busy || sendRetry.isWaiting) return;
        setState((prev) => ({ ...prev, busy: true, error: null, notice: null }));

        const method = state.channel === EMAIL ? EMAIL : state.phoneMethod;
        const body = { channel: state.channel, method: method };
        /**
         * `destination` נשלח רק בהחלפה.
         *
         * שליחת הערך הקיים נרשמת בשרת כשינוי - היא מייצרת אירוע `CONTACT_CHANGED`
         * וכתיבה לבילינג, כלומר הודעה ללקוח על שינוי שלא קרה.
         */
        if (state.changing) {
            body.destination = state.destination.trim();
        }

        try {
            const reply = await axios.post(API_URL + 'sendContactVerificationCode', body, requestConfig());
            if (reply?.data?.responseStatus === API_OK) {
                /**
                 * נשמר היעד שאליו הקוד יצא **בפועל**, ולא נגזר שוב במסך הבא.
                 *
                 * גזירה מחדש הייתה מציגה את הפרט שבשדה הקלט ברגע הזה - ולקוח שהתחרט
                 * והקליד ערך אחר אחרי השליחה היה רואה כתובת שהקוד לא נשלח אליה.
                 */
                // קוד חדש - הספירה על הניחושים אינה רלוונטית לו.
                submitRetry.reset();
                setState((prev) => ({
                    ...prev,
                    busy: false,
                    phase: PHASE_CODE,
                    code: "",
                    error: null,
                    notice: isResend ? "A new code was sent" : null,
                    sentMethod: method,
                    /**
                     * הערך שהשרת מסר - המנורמל - ולא מה שהוקלד.
                     *
                     * הלקוח מקיש `0539004444` והקוד יוצא ל-`+972539004444`. הצגת מה
                     * שהוקלד הייתה מציגה משהו שאינו מה שקרה, ולקוח שמשווה מול ההודעה
                     * שקיבל רואה שני מספרים שונים.
                     */
                    sentTo: reply?.data?.destination
                        || (prev.changing ? prev.destination.trim() : (channelState(prev.channel)?.current ?? "")),
                }));
                /*
                 * בשליחה הראשונה השדה עדיין אינו על המסך, ואת הסמן מציב המפתח
                 * שהשתנה; בשליחה חוזרת השדה כבר שם, וזו הקריאה שמחזירה אליו.
                 */
                focusCode();
                return;
            }
            showReplyError(reply);
        } catch (e) {
            showRequestError();
        }
    };

    const confirmCode = async () => {
        if (state.busy || submitRetry.isWaiting || !state.code.trim()) return;
        setState((prev) => ({ ...prev, busy: true, error: null, notice: null }));

        try {
            const reply = await axios.post(
                API_URL + 'confirmContactVerification',
                { code: state.code.trim() },
                requestConfig()
            );
            if (reply?.data?.responseStatus === API_OK) {
                /**
                 * טוענים מחדש במקום לצאת: יכול להיות ערוץ נוסף שעדיין חייבים.
                 *
                 * `loadState` יוצא מעצמו ברגע שאין חוב (`owesVerification === false`),
                 * ולכן זו נקודה אחת שמכריעה גם "יש עוד" וגם "סיימנו" - ולא שני מקומות
                 * שצריכים להסכים.
                 */
                await loadState();
                return;
            }
            showReplyError(reply);
        } catch (e) {
            showRequestError();
        }
    };

    /**
     * לאן ממשיכים אחרי שאומת.
     *
     * **`landOrResolve` ולא `landAfterLogin`, וזה ההבדל שבין מסך שעובד לבין מסך
     * שמעביר את הלקוח לממשק שדוחה אותו.** המסך הזה משחרר שער אחד, ולסשן יכול להיות
     * שער שני: לקוח שחייב גם לרשום שיטת אימות הונחת בממשק וקיבל
     * `two_fa_enrollment_required` על כל קריאה - בלי שום הודעה שמסבירה למה.
     *
     * הדגלים מגיעים מ-`getContactVerificationState` (`Authorization.sessionObligations`),
     * כלומר בלי סבב רשת נוסף, ו-`blockedSessionRoute` בוחר את היעד באותו סדר קבוע
     * שכל שאר המסלולים משתמשים בו.
     *
     * וכשאין חוב נוסף - `landAfterLogin` מבפנים מכבד גם `redirect_from` וגם את ממשק
     * ברירת המחדל של הריסיילר, כלומר הלקוח נוחת במקום שאליו ניסה להגיע מלכתחילה.
     */
    const leaveVerification = (replyData) => {
        landOrResolve({ ...(replyData || {}), token: userToken() }, navigate, "/successfully-logged-in");
    };

    /**
     * יציאה - **תמיד זמינה, בכל שלב במסך**.
     *
     * המסך הזה חוסם: כל עוד הלקוח עליו הוא אינו מגיע לשום ממשק. בלי דרך החוצה, לקוח
     * שהתחבר בטעות לחשבון הלא נכון - או שאינו יכול לאמת עכשיו - נשאר תקוע בלי אפילו
     * להתנתק. אותה יציאה בדיוק, ומאותו נימוק, קיימת במסך רישום שיטת האימות.
     *
     * `logout` ולא ניווט בלבד: הטוקן שנוצר בהתחברות עדיין חי, ומסך התחברות שהיה נטען
     * עם טוקן חי היה מחזיר את הלקוח לכאן מיד.
     */
    const handleCancelClick = (e) => {
        e.preventDefault();
        if (state.busy) {
            return;
        }
        dispatch(logout());
        navigate("/login", { replace: true });
    };

    const backToPick = () => {
        // חזרה לבחירה - וההודעה שמסבירה את השיחה שייכת לשלב שננטש.
        setState((prev) => ({ ...prev, phase: PHASE_PICK, code: "", error: null, notice: null, sentMethod: null }));
    };

    const current = channelState(state.channel);
    const canSend = state.changing ? state.destination.trim().length > 0 : Boolean(current?.current);

    /**
     * Enter ממשיך לשלב הבא - שליחת הקוד, ואחריה אימותו.
     *
     * **השער הוא של השלב, ולא של שניהם ביחד**, כי שני הטיימרים כאן אינם קשורים זה
     * לזה: `sendRetry` הוא הצינון שבין שליחה לשליחה חוזרת, ו-`submitRetry` הוא זה
     * שבין ניסיון אימות לניסיון הבא. תנאי אחד שדורש את שניהם היה מכבה את Enter
     * לאורך כל צינון השליחה - כלומר בדיוק בדקה שבה מקלידים את הקוד שזה עתה נשלח.
     *
     * אלה אותם שערים שעל הכפתורים, וגם `sendCode` ו-`confirmCode` בודקים אותם
     * בעצמם. Enter אינו דרך לעקוף דבר. ראה `useEnterSubmit`.
     */
    useEnterSubmit(
        () => (state.phase === PHASE_CODE ? confirmCode() : (canSend ? sendCode(false) : undefined)),
        state.phase === PHASE_CODE
            ? !state.busy && !submitRetry.isWaiting
            : !state.busy && !sendRetry.isWaiting && canSend
    );

    return (
        <Frame>
            <div className='main main-verify-contact'>
                <div className='main-top'>
                    <h1>{t("Verify your contact details")}</h1>
                </div>
                <div className='main-body'>
                    <div className='right-main'>
                        <div className='right-main-inner'>
                            <h2>{t("So that we can tell you about changes to your account, please verify an email address or a phone number.")}</h2>

                            {state.loading ? (
                                <h2>{t("Working...")}</h2>
                            ) : state.phase === PHASE_PICK ? (
                                <>
                                    {/*
                                      * הבורר מציג רק את מה שנותר לאמת, ורק כשיש יותר מאחד.
                                      * ערוץ שאומת כבר, או שהריסיילר אינו דורש, אינו בחירה.
                                      */}
                                    {state.outstanding.length > 1 && (
                                        <div className='id-type-row'>
                                            {state.outstanding.map((name) => (
                                                <button
                                                    key={name}
                                                    type='button'
                                                    className={
                                                        'verify-choice' +
                                                        (state.channel === name ? ' verify-choice-selected' : '')
                                                    }
                                                    onClick={() => selectChannel(name)}
                                                >
                                                    {name === EMAIL ? t("Email") : t("Phone")}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {state.changing ? (
                                        <div className='input'>
                                            <input
                                                type={state.channel === EMAIL ? 'email' : 'tel'}
                                                dir='ltr'
                                                value={state.destination}
                                                placeholder={state.channel === EMAIL ? t("New email address") : t("New phone number")}
                                                onChange={(e) => setState((prev) => ({ ...prev, destination: e.target.value }))}
                                            />
                                        </div>
                                    ) : (
                                        <h2>
                                            {t("A verification code will be sent to")}
                                            <span className='verify-destination'>{current?.current}</span>
                                        </h2>
                                    )}

                                    {/* לטלפון שני ערוצים, וזו בחירה של הלקוח ולא נגזרת של הסוג. */}
                                    {state.channel === PHONE && (
                                        <div className='id-type-row'>
                                            <button
                                                type='button'
                                                className={'verify-choice' + (state.phoneMethod === 'SMS' ? ' verify-choice-selected' : '')}
                                                onClick={() => setState((prev) => ({ ...prev, phoneMethod: 'SMS' }))}
                                            >
                                                {t("By SMS")}
                                            </button>
                                            <button
                                                type='button'
                                                className={'verify-choice' + (state.phoneMethod === 'VOICE' ? ' verify-choice-selected' : '')}
                                                onClick={() => setState((prev) => ({ ...prev, phoneMethod: 'VOICE' }))}
                                            >
                                                {t("By phone call")}
                                            </button>
                                        </div>
                                    )}

                                    {/*
                                      * `() => sendCode(false)` ולא `sendCode`: מסירת הפונקציה
                                      * עצמה ל-onClick הייתה מוסרת לה את אירוע הלחיצה כארגומנט
                                      * הראשון, וכל שליחה ראשונה הייתה נחשבת שליחה חוזרת.
                                      */}
                                    <div
                                        className='button button-teal'
                                        onClick={canSend && !state.busy && !sendRetry.isWaiting ? () => sendCode(false) : undefined}
                                    >
                                        {state.busy ? t("Sending...") : t("Send code")}
                                    </div>


                                    {/*
                                      * המעבר בין "לאמת" ל"להחליף" - וזו הדרך השנייה החוצה.
                                      * מוצג רק כשיש פרט קיים; בלעדיו המסך ממילא מבקש ערך.
                                      */}
                                    {current?.current && (
                                        <h2
                                            className='teal switch-mode'
                                            onClick={() => setChanging(!state.changing)}
                                        >
                                            {state.changing
                                                ? t("Verify the detail already on the account")
                                                : t(state.channel === EMAIL ? "Change the email address" : "Change the phone number")}
                                        </h2>
                                    )}
                                </>
                            ) : (
                                <>
                                    {/*
                                      * לאן נשלח הקוד - בראש שלב ההקלדה.
                                      *
                                      * בלי זה המסך מבקש קוד בלי לומר לאן הוא יצא, והלקוח שממתין
                                      * לסמס שלא הגיע אינו יודע אם המספר שאליו נשלח הוא בכלל שלו.
                                      */}
                                    {/*
                                      * בשיחה אין "הקוד נשלח אל": השיחה יוצאת עכשיו, והמספר
                                      * שעליו הלקוח מסתכל הוא הקוד עצמו. ההודעה אומרת את שניהם -
                                      * לאן מחייגים, ומה לעשות - במקום השורה הזו.
                                      */}
                                    {state.sentMethod === 'VOICE' ? (
                                        <VoiceCallNotice phone={state.sentTo} />
                                    ) : (
                                        <h2>
                                            {t("The code was sent to")}
                                            <span className='verify-destination'>{state.sentTo}</span>
                                        </h2>
                                    )}
                                    <div className='input'>
                                        <input
                                            ref={codeRef}
                                            type='text'
                                            inputMode='numeric'
                                            autoComplete='one-time-code'
                                            dir='ltr'
                                            value={state.code}
                                            placeholder={state.sentMethod === 'VOICE' ? t("Last 4 digits") : t("Enter the code you received")}
                                            onChange={(e) => setState((prev) => ({ ...prev, code: e.target.value }))}
                                        />
                                    </div>
                                    <div
                                        className='button button-teal'
                                        onClick={!state.busy && !submitRetry.isWaiting ? confirmCode : undefined}
                                    >
                                        {state.busy ? t("Checking the code...") : t("Confirm")}
                                    </div>
                                    {/*
                                      * "שלחו לי שוב" - ובלעדיו הדרך היחידה לקוד נוסף הייתה
                                      * "חזרה" ואז "שליחת קוד" מחדש. שני צעדים שאינם נראים
                                      * קשורים למה שהלקוח מחפש, במסך שהוא חסום עד שיעבור אותו.
                                      *
                                      * אותה שליחה בדיוק, לאותו יעד: הערוץ, השיטה והיעד עדיין
                                      * ב-state, ולכן אין כאן מסלול שני שיכול לסטות מהראשון.
                                      *
                                      * חסום כשהתקרה נסגרה, כי לחיצה נוספת אז רק מחזירה סירוב -
                                      * והספירה שרצה מתחת ממילא אומרת כמה נשאר.
                                      *
                                      * **הטקסט אינו משתנה ל"שולחים...".** באותו שלב `busy`
                                      * נדלק גם כשלוחצים "אישור", והקישור היה מכריז על שליחה
                                      * בזמן שמה שרץ הוא בדיקת הקוד. מה שמדווח על השליחה הוא
                                      * האישור שנשאר אחריה.
                                      */}
                                    <h2
                                        className='teal switch-mode'
                                        style={state.busy || sendRetry.isWaiting ? DISABLED_LINK : undefined}
                                        onClick={!state.busy && !sendRetry.isWaiting ? () => sendCode(true) : undefined}
                                    >
                                        <Trans i18nKey="I didn't receive the code, <strong>please resend it</strong>" />
                                    </h2>
                                    <h2 className='teal switch-mode' onClick={backToPick}>
                                        {t("Back")}
                                    </h2>
                                </>
                            )}

                            {/*
                              * ההודעות - במקום אחד, ומיד מתחת לפעולה שגרמה להן.
                              *
                              * קודם הן היו מפוזרות: הספירה לאחור הודפסה ליד הכפתור, והשגיאה
                              * שהסבירה אותה כמה שורות מתחת - עם קישור ביניהן. שתי הודעות על
                              * אותו אירוע, בשני מקומות, שנקראות כשתי תקלות.
                              *
                              * וכשהספירה רצה מוצגת **רק היא**: היא נושאת את אותו הסבר בתוספת
                              * הזמן, ושורה נוספת לצידה חוזרת על עצמה.
                              */}
                            {submitRetry.isWaiting ? (
                                <RetryNotice
                                    retry={submitRetry}
                                    i18nKey='Too many attempts. You can try again in {{time}}'
                                />
                            ) : sendRetry.isWaiting ? (
                                <RetryNotice
                                    retry={sendRetry}
                                    i18nKey='Too many codes were sent. You can ask for another one in {{time}}'
                                />
                            ) : state.error ? (
                                <h2 className='verify-error'>{t(state.error)}</h2>
                            ) : (
                                /*
                                 * האישור על שליחה חוזרת - במקום שבו מוצגות ההודעות, ולא
                                 * לצד הקישור. מסך שאינו משתנה אחרי לחיצה נקרא כלחיצה
                                 * שלא נקלטה, והלקוח לוחץ שוב.
                                 */
                                state.notice && <h2 className='mt20 teal'>{t(state.notice)}</h2>
                            )}

                            {/* היציאה. מחוץ לענפי השלבים, כדי שתהיה בכל אחד מהם. */}
                            <h2 className='teal switch-mode' onClick={(e) => handleCancelClick(e)}>
                                {t("Cancel and return to the login screen")}
                            </h2>
                        </div>
                    </div>
                </div>
            </div>
        </Frame>
    );
}
